import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {ECRepresentative, ECRepresentativeAddress, ECRepresentativeType, EnergyCommunity} from 'smag-commons/cfg/hierarchy';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import * as _ from 'lodash';
import {validateNotBlank} from '../../../commons/validators';
import {HierarchyEntity} from '../../../shared/hierarchy.service';
import {isDefined} from '../../../commons/commons';
import {ConfigurationService} from '../../../shared/configuration.service';
import {EMAIL_PATTERN, MOBILE_PATTERN, NAME_PATTERN, TAX_ID_PATTERN, VAT_NUMBER_PATTERN} from '../../../shared/patterns';
import {
    DEFAULT_MAX_CHARS,
    EMAIL_MAX_CHARS,
    MOBILE_MAX_CHARS,
    NAME_MAX_CHARS,
    TAX_ID_MAX_CHARS,
    VAT_NUMBER_MAX_CHARS
} from '../../../shared/limits';
import {addressToString, EditedRepresentativeMember} from '../ec-representatives.component';
import {AddressExt} from '../../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {mkRepresentativeAddress} from '../../../shared/ec-members';
import {mkTextField} from '../../../shared/energy-community';

const REPRESENTATIVE_TYPES: ECRepresentativeType[] = [
    ECRepresentativeType.NATURAL_PERSON,
    ECRepresentativeType.PRIVATE_INSTITUTION,
    ECRepresentativeType.PUBLIC_INSTITUTION
];


@Component({
    selector: 'app-edit-representative',
    templateUrl: './edit-representative.component.html',
    styleUrls: ['./edit-representative.component.scss']
})
export class EditRepresentativeComponent implements OnInit, OnChanges, OnDestroy {

    private destroyed = new Subject<void>();

    @Input() community: EnergyCommunity;
    @Input() member: ECRepresentative;
    @Output() edited: EventEmitter<EditedRepresentativeMember> = new EventEmitter<EditedRepresentativeMember>();
    @Output() invited: EventEmitter<(member: ECRepresentative) => void> = new EventEmitter<(member: ECRepresentative) => void>();
    @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();

    timeZone: string;
    form: FormGroup;
    podEntities: { [podId: string]: HierarchyEntity };
    podEntitySubscriptions: { [podId: string]: Subscription };
    documents: { [docId: string]: Document };
    representativeTypes: ECRepresentativeType[];
    currentAddress: ECRepresentativeAddress;

    // exported in HTML
    EMAIL_MAX_CHARS = EMAIL_MAX_CHARS;
    MOBILE_MAX_CHARS = MOBILE_MAX_CHARS;
    TAX_ID_MAX_CHARS = TAX_ID_MAX_CHARS;
    NAME_MAX_CHARS = NAME_MAX_CHARS;
    DEFAULT_MAX_CHARS = DEFAULT_MAX_CHARS;
    VAT_NUMBER_MAX_CHARS = VAT_NUMBER_MAX_CHARS;
    isDefined = isDefined;

    constructor(private readonly configurationService: ConfigurationService,
                private readonly fb: FormBuilder) {
        this.timeZone = null;
        this.member = null;
        this.representativeTypes = REPRESENTATIVE_TYPES;
        this.form = this.fb.group({
            firstName: new FormControl(null, [validateNotBlank, Validators.pattern(NAME_PATTERN)]),
            lastName: new FormControl(null, [validateNotBlank, Validators.pattern(NAME_PATTERN)]),
            companyName: new FormControl(null, []),
            representativeType: new FormControl(null, [validateNotBlank]),
            email: new FormControl(null, [validateNotBlank, Validators.pattern(EMAIL_PATTERN)]),
            cf: new FormControl(null, [Validators.pattern(TAX_ID_PATTERN)]),
            piva: new FormControl(null, [Validators.pattern(VAT_NUMBER_PATTERN)]),
            address: new FormControl(null, []),
            mobile: new FormControl(null, [validateNotBlank, Validators.pattern(MOBILE_PATTERN)]),
            phone: new FormControl(null, [Validators.pattern(MOBILE_PATTERN)]),
            pec: new FormControl(null, [Validators.pattern(EMAIL_PATTERN)])
        });
        this.podEntities = {};
        this.podEntitySubscriptions = {};
        this.documents = {};
    }

    ngOnInit() {
        this.form.controls.email.setValidators([Validators.required, Validators.pattern(EMAIL_PATTERN)]);
        // mobile number is optional, once it gets set it can be edited but not deleted
        this.form.controls.mobile.setValidators([validateNotBlank, Validators.pattern(MOBILE_PATTERN)]);
        this.form.valueChanges.pipe(
            takeUntil(this.destroyed)
        ).subscribe(value => {
            const email = mkTextField(value.email);
            this.edited.emit({
                personalData: {
                    email: email ? email.toLowerCase() : null,
                    first_name: mkTextField(value.firstName),
                    last_name: mkTextField(value.lastName),
                    company_name: this.hasCompany ? mkTextField(value.companyName) : undefined,
                    cf: !this.hasCompany ? mkTextField(value.cf) : undefined,
                    piva: this.hasCompany ? mkTextField(value.piva) : undefined,
                    address: {
                        street: this.currentAddress?.street,
                        street_number: this.currentAddress?.street_number,
                        city: this.currentAddress?.city,
                        province: this.currentAddress?.province,
                        postal_code: this.currentAddress?.postal_code,
                    },
                    mobile_number: mkTextField(value.mobile),
                    telephone_number: mkTextField(value.phone),
                    pec: mkTextField(value.pec)
                },
                representativeType: value.representativeType,
                networkIds: this.member.network_ids,
                tenant: this.member.tenant
            });
        });
        this.form.statusChanges.pipe(
            takeUntil(this.destroyed)
        ).subscribe(() => {
            this.valid.emit(this.form.valid);
        });
        this.configurationService.timeConfig.pipe(
            takeUntil(this.destroyed)
        ).subscribe(timeCfg => {
            this.timeZone = timeCfg.time_zone;
            this.setupRepresentative(this.member);
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('member' in changes && !changes.member.isFirstChange() && !!changes.member.currentValue) {
            this.setupRepresentative(changes.member.currentValue);
        }
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get hasCompany(): boolean {
        return (this.member.representative_type === ECRepresentativeType.PRIVATE_INSTITUTION) ||
            (this.member.representative_type === ECRepresentativeType.PUBLIC_INSTITUTION);
    }

    get invalidCompanyName(): boolean {
        return this.form.controls.companyName.dirty && this.form.controls.companyName.invalid;
    }

    get invalidFirstName(): boolean {
        return this.form.controls.firstName.dirty && this.form.controls.firstName.invalid;
    }

    get invalidLastName(): boolean {
        return this.form.controls.lastName.dirty && this.form.controls.lastName.invalid;
    }

    get invalidTaxNumber(): boolean {
        return this.form.controls.cf.dirty && this.form.controls.cf.invalid;
    }

    get invalidVatNumber(): boolean {
        return this.form.controls.piva.dirty && this.form.controls.piva.invalid;
    }
    get addressFormControl(): FormControl {
        return this.form.controls.address as FormControl;
    }
    get invalidMobile(): boolean {
        return this.form.controls.mobile.dirty && this.form.controls.mobile.invalid;
    }

    get invalidPhone(): boolean {
        return this.form.controls.phone.dirty && this.form.controls.phone.invalid;
    }

    get invalidPec(): boolean {
        return this.form.controls.pec.dirty && this.form.controls.pec.invalid;
    }

    private setupRepresentative(member: ECRepresentative) {
        this.member = _.cloneDeep(member);
        // company name and piva only mandatory for `COMPANY` reps
        // fiscal code only mandatory for `NATURAL PERSON` reps
        if (this.member.representative_type === ECRepresentativeType.PRIVATE_INSTITUTION ||
            this.member.representative_type === ECRepresentativeType.PUBLIC_INSTITUTION) {
            this.form.controls.companyName.setValidators([validateNotBlank]);
            this.form.controls.piva.setValidators([Validators.pattern(VAT_NUMBER_PATTERN)]);
            this.form.controls.cf.setValidators([]);
        } else { // this.member.representative_type === ECRepresentativeType.NATURAL_PERSON
            this.form.controls.companyName.setValidators([]);
            this.form.controls.piva.setValidators([]);
            this.form.controls.cf.setValidators([Validators.pattern(TAX_ID_PATTERN)]);
        }
        this.form.setValue({
            firstName: this.member.personal_data.first_name,
            lastName: this.member.personal_data.last_name,
            companyName: this.member.personal_data.company_name || null,
            representativeType: this.member.representative_type,
            email: this.member.personal_data.email,
            cf: this.member.personal_data.cf || null,
            piva: this.member.personal_data.piva || null,
            address: addressToString(this.member.personal_data.address),
            mobile: this.member.personal_data.mobile_number,
            phone: this.member.personal_data.telephone_number || null,
            pec: this.member.personal_data.pec || null
        });
    }

    addressChange(event: AddressExt|null) {
        this.currentAddress = mkRepresentativeAddress(event);
        this.addressFormControl.setValue(this.currentAddress);
    }
}




